import {
  Alert,
  AlertIcon,
  Box,
  Checkbox,
  FormControl,
  FormErrorMessage,
  FormHelperText,
  FormLabel,
  Heading,
  ListItem,
  Radio,
  RadioGroup,
  Spinner,
  Stack,
  Text,
  UnorderedList,
} from "@chakra-ui/react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  NegotiationImpossibleCase,
  type NegotiationWithoutNoticeDto,
  Regulation,
  type SourcingEventDto,
  type SourcingEventPatchDto,
  useGetNegotiationWithoutNoticeQuery,
  type FoaPart,
} from "../../../../../autogen/bff-api";
import { useEditor } from "../../../../../common/editing/save-coordinator";
import { useFieldErrorTranslation } from "../../../../../common/editing/save-coordinator/useFieldErrorTranslation";
import { TipTap } from "../../../../../common/input/TipTap/TipTap";
import { ListLoadError } from "../../../../../common/ListLoadError";
import {
  OrganizationsSelector,
  type SelectableOrganization,
} from "../../../../../common/organization/OrganizationsSelector";
import { useDisplayDate } from "../../../../../common/formatting/useDisplayDate";
import { negotiationCaseLabel, negotiationCaseProvision, negotiationImpossibleCasesFor } from "../utils/noCompetition";
import { followsDesignContest, followsFailedCompetition, invitesPrecedingTenderers } from "../utils/negotiationWithoutNotice";

const TEXT_DEBOUNCE_MS = 400;
const FIELD_PATH = "publicContext.negotiationWithoutNotice";

const limitedRationaleToolbar = {
  underline: false,
  headings: false,
  colors: false,
  link: false,
  table: false,
  image: false,
  template: false,
};

interface Props {
  eventId: string;
  negotiation: NegotiationWithoutNoticeDto | null;
  regulation: Regulation | null;
  contestBelowEeaTier?: boolean;
  isEditable: boolean;
}

export const NegotiationWithoutNoticeField = ({ eventId, negotiation, regulation, contestBelowEeaTier, isEditable }: Props) => {
  const { t } = useTranslation();
  const displayDate = useDisplayDate();
  const { updateField, fieldStatus, fieldError } = useEditor<SourcingEventDto, SourcingEventPatchDto>(eventId);
  const formatFieldError = useFieldErrorTranslation();
  const precedingError = fieldError(`${FIELD_PATH}.precedingEventId`);
  const inviteesError = fieldError(`${FIELD_PATH}.invitedOrganizationIds`);
  const { data: overview, isLoading, isFetching, isError, refetch } = useGetNegotiationWithoutNoticeQuery({ eventId });
  const savedInvitees: SelectableOrganization[] = (overview?.invitees ?? []).map((supplier) => ({
    id: supplier.organizationId,
    name: supplier.name,
  }));
  const [picked, setPicked] = useState<SelectableOrganization[] | null>(null);
  const savedIds = savedInvitees.map((organization) => organization.id).join(",");
  useEffect(() => {
    if (picked && picked.map((organization) => organization.id).join(",") === savedIds) setPicked(null);
  }, [picked, savedIds]);
  const shownInvitees = picked ?? savedInvitees;
  const status = fieldStatus(FIELD_PATH);
  const isSaving = status === "saving" || status === "pending";
  const chosenCase = negotiation?.case ?? null;
  const afterFailedCompetition = followsFailedCompetition(chosenCase);
  const afterDesignContest = followsDesignContest(chosenCase);
  const chosenPreceding = negotiation?.precedingEventId ?? null;
  const offeredPreceding = afterDesignContest
    ? (overview?.designContests ?? []).map((contest) => contest.eventId)
    : (overview?.precedingCompetitions ?? []).map((competition) => competition.eventId);
  const chosenNoLongerQualifies = chosenPreceding != null && overview != null && !offeredPreceding.includes(chosenPreceding);

  const save = (changes: Partial<NegotiationWithoutNoticeDto>, debounceMs?: number) => {
    const next: NegotiationWithoutNoticeDto = {
      case: chosenCase,
      rationale: negotiation?.rationale ?? null,
      precedingEventId: negotiation?.precedingEventId ?? null,
      invitedOrganizationIds: negotiation?.invitedOrganizationIds ?? [],
      unchangedBasisConfirmed: negotiation?.unchangedBasisConfirmed ?? null,
      ...changes,
    };
    const sameKindOfPrecedingEvent =
      followsFailedCompetition(next.case) === followsFailedCompetition(chosenCase) &&
      followsDesignContest(next.case) === followsDesignContest(chosenCase);
    if (!sameKindOfPrecedingEvent) next.precedingEventId = null;
    if (!followsFailedCompetition(next.case)) next.unchangedBasisConfirmed = null;
    updateField({ publicContext: { negotiationWithoutNotice: next } }, debounceMs ? { debounceMs } : undefined);
  };

  return (
    <Box borderWidth="1px" borderColor="smBorder" borderRadius="lg" bg="white" p="5">
      <Heading as="h3" size="sm">
        {t("Grounds for negotiating without a notice")} {isSaving && <Spinner size="xs" />}
      </Heading>
      <Text fontSize="sm" color="smMuted" mt="1">
        {t("The competition is not announced, so it may only run when one of these conditions is met.")}
      </Text>

      <FormControl mt="5" isDisabled={!isEditable} isRequired>
        <FormLabel fontSize="sm">{t("Which condition applies?")}</FormLabel>
        <RadioGroup value={chosenCase ?? ""} onChange={(value) => save({ case: value as NegotiationImpossibleCase })}>
          <Stack spacing="3">
            {negotiationImpossibleCasesFor(regulation).map((option) => (
              <Radio key={option} value={option} colorScheme="teal" alignItems="flex-start">
                <Text fontSize="sm">{negotiationCaseLabel(option, regulation, t)}</Text>
                <Text fontSize="xs" color="smMuted" fontStyle="italic">
                  {negotiationCaseProvision(option, regulation, t, contestBelowEeaTier)}
                </Text>
              </Radio>
            ))}
          </Stack>
        </RadioGroup>
      </FormControl>

      <FormControl mt="5" isDisabled={!isEditable} isRequired>
        <FormLabel fontSize="sm">{t("Justification")}</FormLabel>
        {chosenCase == null ? (
          <Box borderWidth="1px" borderColor="smBorder" borderRadius="md" p="4">
            <Text fontSize="sm" color="smMuted">
              {t("Choose which condition applies before writing the justification.")}
            </Text>
          </Box>
        ) : (
          <TipTap
            content={negotiation?.rationale ?? ""}
            onUpdate={(rationale: string) => save({ rationale }, TEXT_DEBOUNCE_MS)}
            toolbar={limitedRationaleToolbar}
            debounceWait={0}
            editable={isEditable}
          />
        )}
        <FormHelperText>
          {chosenCase === NegotiationImpossibleCase.ExtremeUrgency
            ? t(
                "Describe which circumstances could not be foreseen, why they are not caused by the contracting authority, and why the deadlines of an ordinary procedure could not be met. The contract may not be larger than strictly necessary.",
              )
            : t("Carried into the procurement protocol and the intention notice — you will not be asked to write it again.")}
        </FormHelperText>
      </FormControl>

      {isLoading ? (
        <Spinner size="sm" mt="5" />
      ) : isError || !overview ? (
        <Box mt="5">
          <ListLoadError message={t("Could not load the suppliers and competitions.")} onRetry={refetch} />
        </Box>
      ) : (
        <>
          {afterFailedCompetition && (
            <FormControl mt="5" isDisabled={!isEditable} isRequired isInvalid={!!precedingError || chosenNoLongerQualifies}>
              <FormLabel fontSize="sm">{t("Which competition failed?")}</FormLabel>
              {chosenNoLongerQualifies && (
                <Text fontSize="sm" color="smError" mb="2">
                  {t(
                    "The competition you chose no longer qualifies: it must be your own, announced under this procurement's regulation, and cancelled. Choose another.",
                  )}
                </Text>
              )}
              {overview.precedingCompetitions.length === 0 ? (
                <Text fontSize="sm" color="smMuted">
                  {regulation === Regulation.Forsyningsforskriften
                    ? t("You have no cancelled announced competition this procedure can follow.")
                    : t("You have no cancelled open or restricted tender this procedure can follow.")}
                </Text>
              ) : (
                <RadioGroup
                  value={negotiation?.precedingEventId ?? ""}
                  onChange={(value) => save({ precedingEventId: value })}
                >
                  <Stack spacing="3">
                    {overview.precedingCompetitions.map((competition) => (
                      <Radio key={competition.eventId} value={competition.eventId} colorScheme="teal" alignItems="flex-start">
                        <Text fontSize="sm">{competition.title}</Text>
                        <Text fontSize="xs" color="smMuted">
                          {competition.cancelledAt
                            ? t("Cancelled {{date}}", { date: displayDate(competition.cancelledAt) })
                            : t("Cancelled_competition")}
                          {" · "}
                          {t("{{count}} tenderers not rejected", { count: competition.tenderers.length })}
                        </Text>
                      </Radio>
                    ))}
                  </Stack>
                </RadioGroup>
              )}
              {precedingError && <FormErrorMessage>{formatFieldError(precedingError)}</FormErrorMessage>}
            </FormControl>
          )}

          {afterDesignContest && (
            <FormControl mt="5" isDisabled={!isEditable} isRequired isInvalid={!!precedingError || chosenNoLongerQualifies}>
              <FormLabel fontSize="sm">{t("Which design contest does it follow?")}</FormLabel>
              {chosenNoLongerQualifies && (
                <Text fontSize="sm" color="smError" mb="2">
                  {t(
                    "The design contest you chose no longer qualifies: it must be your own, with winners chosen, and announced with a follow-up service contract under this procurement's regulation. Choose another.",
                  )}
                </Text>
              )}
              {(overview.designContests ?? []).length === 0 ? (
                <Text fontSize="sm" color="smMuted">
                  {t("You have no design contest with chosen winners that announced a follow-up service contract.")}
                </Text>
              ) : (
                <RadioGroup
                  value={negotiation?.precedingEventId ?? ""}
                  onChange={(value) => save({ precedingEventId: value })}
                >
                  <Stack spacing="3">
                    {(overview.designContests ?? []).map((contest) => (
                      <Radio key={contest.eventId} value={contest.eventId} colorScheme="teal" alignItems="flex-start">
                        <Text fontSize="sm">{contest.title}</Text>
                        <Text fontSize="xs" color="smMuted">
                          {contest.winnersChosenAt
                            ? t("Winners chosen {{date}}", { date: displayDate(contest.winnersChosenAt) })
                            : t("Winners chosen")}
                          {" · "}
                          {t("{{count}} winners", { count: contest.winners.length })}
                        </Text>
                      </Radio>
                    ))}
                  </Stack>
                </RadioGroup>
              )}
              {precedingError && <FormErrorMessage>{formatFieldError(precedingError)}</FormErrorMessage>}
            </FormControl>
          )}

          <FormControl mt="5" isDisabled={!isEditable} isRequired isInvalid={!!inviteesError}>
            <FormLabel fontSize="sm">{t("Suppliers invited to negotiate")}</FormLabel>
            {afterDesignContest ? (
              <>
                <Text fontSize="sm" color="smMuted" mb="2">
                  {regulation === Regulation.Forsyningsforskriften
                    ? t("Every winner of the design contest is invited, and nobody else (forsyningsforskriften § 9-2 c).")
                    : t("Every winner of the design contest is invited, and nobody else (FOA § 13-3 d).")}
                </Text>
                {!negotiation?.precedingEventId || chosenNoLongerQualifies ? (
                  <Text fontSize="sm" color="smMuted">
                    {t("Choose the design contest to see who is invited.")}
                  </Text>
                ) : overview.invitees.length > 0 ? (
                  <UnorderedList fontSize="sm" spacing="0.5">
                    {overview.invitees.map((supplier) => (
                      <ListItem key={supplier.organizationId}>{supplier.name}</ListItem>
                    ))}
                  </UnorderedList>
                ) : isFetching ? (
                  <Spinner size="sm" />
                ) : (
                  <Text fontSize="sm" color="smError">
                    {t("Nobody can be invited from that design contest. Choose another contest.")}
                  </Text>
                )}
                {inviteesError && <FormErrorMessage>{formatFieldError(inviteesError)}</FormErrorMessage>}
              </>
            ) : invitesPrecedingTenderers(chosenCase) ? (
              <>
                <Text fontSize="sm" color="smMuted" mb="2">
                  {t(
                    "Everyone who tendered in the failed competition and was not rejected is invited, and nobody else (FOA § 13-3 a).",
                  )}
                </Text>
                {chosenNoLongerQualifies ? (
                  <Text fontSize="sm" color="smMuted">
                    {t("Choose the failed competition to see who is invited.")}
                  </Text>
                ) : overview.invitees.length > 0 ? (
                  <UnorderedList fontSize="sm" spacing="0.5">
                    {overview.invitees.map((supplier) => (
                      <ListItem key={supplier.organizationId}>{supplier.name}</ListItem>
                    ))}
                  </UnorderedList>
                ) : negotiation?.precedingEventId ? (
                  <Text fontSize="sm" color="smError">
                    {t(
                      "Nobody who tendered in that competition can be invited. Choose another competition, or «no or no suitable tenders» if none of the tenders could be used.",
                    )}
                  </Text>
                ) : (
                  <Text fontSize="sm" color="smMuted">
                    {t("Choose the failed competition to see who is invited.")}
                  </Text>
                )}
              </>
            ) : (
              <>
                <OrganizationsSelector
                  selectedOrganizations={shownInvitees}
                  onChange={({ organizations }) => {
                    const chosen = organizations ?? [];
                    setPicked(chosen);
                    save({ invitedOrganizationIds: chosen.map((organization) => organization.id) });
                  }}
                />
                {inviteesError && <FormErrorMessage>{formatFieldError(inviteesError)}</FormErrorMessage>}
                <FormHelperText>
                  {t("The suppliers are invited when you open the competition. Their owners and admins are notified.")}
                </FormHelperText>
              </>
            )}
          </FormControl>
        </>
      )}

      {afterFailedCompetition && (
        <Alert status="warning" borderRadius="lg" mt="5" alignItems="flex-start">
          <AlertIcon />
          <Box>
            <Text fontSize="sm">
              {regulation === Regulation.Forsyningsforskriften
                ? t(
                    "The specifications and contract terms may not be changed substantially (forsyningsforskriften § 9-2 a). Confirm that the changes are not substantial.",
                  )
                : t(
                    "The specifications and contract terms may not be changed substantially (§ 13-3 a/b). Confirm that the changes are not substantial.",
                  )}
            </Text>
            <Checkbox
              mt="2"
              isChecked={negotiation?.unchangedBasisConfirmed === true}
              isDisabled={!isEditable}
              onChange={(event) => save({ unchangedBasisConfirmed: event.target.checked })}
            >
              <Text fontSize="sm">{t("The specifications and contract terms are not substantially changed")}</Text>
            </Checkbox>
          </Box>
        </Alert>
      )}
    </Box>
  );
};
